import { getEffectiveAgeRangeForCategory } from "../../../utils/dates";
import { ParticipantFilteringOptions } from "../types/PresentationFormTypes";
import { ParticipantWithId } from "../../../interfaces/IParticipants";
import { useCallback, useMemo } from "react";
import { useContestContext } from "../../../contexts/ContestContext";
/**
 * ============================================================================
 * USE PARTICIPANT FILTERING - HOOK DO FILTROWANIA ZAWODNIKÓW
 * ============================================================================
 *
 * CEL HOOKA:
 * - Filtruje listę zawodników na podstawie ograniczeń kategorii (wiek, płeć)
 * - Wyklucza zawodników już wybranych w innych prezentacjach w tej kategorii
 * - Obsługuje tryb "pokaż wszystkich" (ignoruje filtry)
 * - Zapewnia spójne reguły biznesowe dla wyboru zawodników
 *
 * ARCHITEKTURA:
 * - Custom Hook Pattern: Enkapsuluje logikę filtrowania w reusable hook
 * - Business Rules Engine: Implementuje reguły biznesowe dla wyboru zawodników
 * - Performance Optimization: Używa useMemo i useCallback dla optymalizacji
 *
 * REGUŁY FILTROWANIA:
 * 1. Ograniczenia wiekowe: minParticipantBirthDate i maxParticipantBirthDate
 * 2. Ograniczenia płci: category.gender (null = bez ograniczeń)
 * 3. Wykluczenie już wybranych: zawodnicy z innych prezentacji w kategorii
 * 4. Tryb "pokaż wszystkich": ignoruje wszystkie filtry
 *
 * PRZEPŁYW DANYCH:
 * 1. Pobiera opcje filtrowania (kategoria, istniejące prezentacje, tryb)
 * 2. Identyfikuje już wybranych zawodników w kategorii
 * 3. Filtruje listę zawodników na podstawie reguł
 * 4. Zwraca przefiltrowaną listę i statystyki
 */

export const useParticipantFiltering = (options: ParticipantFilteringOptions) => {
  const {
    category,
    existingPresentations,
    showAll,
    currentPresentationId,
    selectedGroupId,
    groupParticipantIds,
  } = options;

  const { contest } = useContestContext();

  const getAlreadySelectedIds = useCallback((): string[] => {
    const alreadySelectedIds = new Set<string>();

    existingPresentations.forEach((presentation) => {
      // Pomiń aktualnie edytowaną prezentację
      if (presentation.id === currentPresentationId) {
        return;
      }

      presentation.participants?.forEach((participant) => {
        if (participant.id) {
          alreadySelectedIds.add(participant.id);
        }
      });
    });

    return Array.from(alreadySelectedIds);
  }, [existingPresentations, currentPresentationId]);

  // ============================================================================
  // GŁÓWNA FUNKCJA FILTROWANIA ZAWODNIKÓW
  // ============================================================================

  // Filtruje listę zawodników na podstawie ograniczeń kategorii i reguł biznesowych
  // Wejście: participants (lista wszystkich zawodników)
  // Wyjście: ParticipantWithId[] (przefiltrowana lista zawodników)
  // Reguły filtrowania:
  // 1. Jeśli brak kategorii - zwróć wszystkich zawodników
  // 2. Jeśli showAll = true - ignoruj wszystkie filtry
  // 3. Wyklucz zawodników już wybranych w innych prezentacjach
  // 4. Sprawdź ograniczenia wiekowe (minParticipantBirthDate, maxParticipantBirthDate)
  // 5. Sprawdź ograniczenia płci (category.gender)
  const filterParticipants = useCallback(
    (participants: ParticipantWithId[]): ParticipantWithId[] => {
      // Jeśli brak kategorii - nie ma ograniczeń, zwróć wszystkich
      if (!category) {
        return participants;
      }

      let filtered = participants;

      // Filtrowanie po grupie (jeśli wybrana i nie w trybie "pokaż wszystkich")
      if (groupParticipantIds && groupParticipantIds.length > 0 && !showAll) {
        filtered = filtered.filter((p) => p.id && groupParticipantIds.includes(p.id));
      }

      // Pobierz ID zawodników już wybranych w innych prezentacjach
      const alreadySelectedIds = getAlreadySelectedIds();

      filtered = filtered.filter((participant) => {
        // Wyklucz zawodników już wybranych w innych prezentacjach w tej kategorii
        if (alreadySelectedIds.includes(participant.id || "")) {
          return false;
        }

        // Filtrowanie według wieku z uwzględnieniem globalnych offsetów
        const birthDate = new Date(participant.birthDate);
        const { minDate, maxDate } = getEffectiveAgeRangeForCategory(category, contest?.settings);

        if (minDate && birthDate < minDate) {
          return false;
        }
        if (maxDate && birthDate > maxDate) {
          return false;
        }

        // Filtrowanie po płci kategorii
        if (category.gender !== null && category.gender !== undefined) {
          if (participant.gender !== category.gender) {
            return false;
          }
        }

        return true;
      });

      return filtered;
    },
    [category, contest, getAlreadySelectedIds, showAll, groupParticipantIds],
  );

  // ============================================================================
  // STATYSTYKI JUŻ WYBRANYCH ZAWODNIKÓW
  // ============================================================================

  // Oblicza liczbę zawodników już wybranych w innych prezentacjach
  // Wejście: Brak (używa getAlreadySelectedIds)
  // Wyjście: number (liczba już wybranych zawodników)
  // Używane do wyświetlania informacji użytkownikowi
  const alreadySelectedCount = useMemo(() => {
    return getAlreadySelectedIds().length;
  }, [getAlreadySelectedIds]);

  // ============================================================================
  // EKSPORT INTERFEJSU HOOKA
  // ============================================================================

  // Zwraca funkcje i dane potrzebne do filtrowania zawodników
  // Używane przez komponenty formularzy do kontrolowania dostępności zawodników
  return {
    filterParticipants, // Główna funkcja filtrowania zawodników
    alreadySelectedCount, // Liczba już wybranych zawodników (dla UI)
    getAlreadySelectedIds, // Funkcja pobierania ID już wybranych zawodników
  };
};
